import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { 
  Save, 
  Type,
  FileText,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const defaultTexts = {
  hero_subtitle: {
    sl: 'S spoštovanjem do divjadi, z odgovornostjo do narave.',
    en: 'With respect for wildlife, with responsibility for nature.'
  },
  about_description: {
    sl: 'Lovska družina Rakek je ena najstarejših in z 81 člani ena največjih lovskih organizacij v Zvezi lovskih družin Notranjske. Naša poslanstvo je trajnostno upravljanje z divjadjo in ohranjanje naravnega ravnovesja v naših gozdovih.',
    en: 'Hunting Society Rakek is one of the oldest and with 81 members one of the largest hunting organizations in the Association of Hunting Societies of Notranjska. Our mission is sustainable wildlife management and preserving the natural balance in our forests.'
  },
  hunting_area_description: {
    sl: 'Naše lovišče obsega 4.911,34 hektarjev skupne površine, od tega 4.703,30 ha lovne površine. Raznolik teren od gostih gozdov do travnikov in mokrišč je dom bogati favni.',
    en: 'Our hunting ground covers 4,911.34 hectares of total area, of which 4,703.30 ha is hunting area. The diverse terrain from dense forests to meadows and wetlands is home to rich fauna.'
  },
  wildlife_intro: {
    sl: 'Lovišče Rakek se razteza čez razgibano pokrajino, ki nudi zavetje številnim vrstam divjadi. Naše upravljanje z loviščem temelji na spoštovanju naravnih procesov.',
    en: 'The Rakek hunting ground stretches across a diverse landscape that provides shelter to numerous wildlife species. Our management is based on respect for natural processes.'
  },
  wildlife_commitment: {
    sl: 'Lovci LD Rakek si bomo še naprej prizadevali za strokovno spremljanje vseh vrst divjadi in aktivno reševanje izzivov, ki jih prinašajo spremembe v naravnem okolju.',
    en: 'The hunters of LD Rakek will continue to strive for professional monitoring of all wildlife species and actively address the challenges brought by changes in the natural environment.'
  }
};

const textFields = [
  {
    key: 'hero_subtitle',
    label: 'Slogan (pod naslovom)',
    type: 'input'
  },
  {
    key: 'about_description',
    label: 'Opis "O nas"',
    type: 'textarea'
  },
  {
    key: 'hunting_area_description',
    label: 'Opis lovišča',
    type: 'textarea'
  },
  {
    key: 'wildlife_intro',
    label: 'Uvod v sekcijo divjadi',
    type: 'textarea'
  },
  {
    key: 'wildlife_commitment',
    label: 'Naša zaveza (divjad)',
    type: 'textarea'
  }
];

const AdminTexts = () => {
  const [texts, setTexts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchTexts();
  }, []);

  const fetchTexts = async () => {
    try {
      const response = await axios.get(`${API}/texts`);
      const merged = {};
      
      textFields.forEach(field => {
        merged[field.key] = {
          sl: response.data[field.key]?.sl || defaultTexts[field.key]?.sl || '',
          en: response.data[field.key]?.en || defaultTexts[field.key]?.en || ''
        };
      });
      
      setTexts(merged);
    } catch (error) {
      console.error('Error fetching texts:', error);
      // Use defaults
      setTexts(defaultTexts);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const items = Object.entries(texts).map(([key, value]) => ({
        key,
        value_sl: value.sl,
        value_en: value.en
      }));
      
      await axios.post(`${API}/texts/batch`, { items });
      toast.success('Besedila so bila shranjena');
    } catch (error) {
      console.error('Error saving texts:', error);
      toast.error('Napaka pri shranjevanju');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (key, lang, value) => {
    setTexts(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [lang]: value
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Type className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-semibold text-white">Besedila strani</h2>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-500"
        >
          {saving ? (
            <span className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Shranjujem...
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <Save className="w-4 h-4" />
              Shrani vse
            </span>
          )}
        </Button>
      </div>

      {/* Text Fields */}
      <div className="space-y-6">
        {textFields.map((field) => (
          <Card key={field.key} className="bg-stone-800/50 border-stone-700/50 p-6">
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-400" />
                <Label className="text-white font-semibold">{field.label}</Label>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Slovenian */}
                <div className="space-y-2">
                  <Label className="text-stone-400 text-sm">Slovensko</Label>
                  {field.type === 'textarea' ? (
                    <Textarea
                      value={texts[field.key]?.sl || ''}
                      onChange={(e) => handleChange(field.key, 'sl', e.target.value)}
                      rows={4}
                      className="bg-stone-900/50 border-stone-700 text-white resize-none"
                    />
                  ) : (
                    <Input
                      value={texts[field.key]?.sl || ''}
                      onChange={(e) => handleChange(field.key, 'sl', e.target.value)}
                      className="bg-stone-900/50 border-stone-700 text-white"
                    />
                  )}
                </div>

                {/* English */}
                <div className="space-y-2">
                  <Label className="text-stone-400 text-sm">Angleško</Label>
                  {field.type === 'textarea' ? (
                    <Textarea
                      value={texts[field.key]?.en || ''}
                      onChange={(e) => handleChange(field.key, 'en', e.target.value)}
                      rows={4}
                      className="bg-stone-900/50 border-stone-700 text-white resize-none"
                    />
                  ) : (
                    <Input
                      value={texts[field.key]?.en || ''}
                      onChange={(e) => handleChange(field.key, 'en', e.target.value)}
                      className="bg-stone-900/50 border-stone-700 text-white"
                    />
                  )}
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default AdminTexts;
